import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const adminRole = (localStorage.getItem('adminRole') || 'SUPER_ADMIN').toUpperCase();

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRole');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/user-activity', label: 'User Activity', icon: '🔥' },
    { path: '/admin-management', label: 'Admin Management', icon: '🔐' },
    { path: '/users', label: 'Users', icon: '👥' },
    { path: '/impressions', label: 'Impressions', icon: '👀' },
    { path: '/deleted-accounts', label: 'Deleted Accounts', icon: '🗑️' },
    { path: '/verifications', label: 'Profile Verifications', icon: '✅' },
    { path: '/verified-users', label: 'Verified Users', icon: '🛡️' },
    { path: '/app-promotions', label: 'App Promotions', icon: '🎉' },
    { path: '/push-notifications', label: 'Push Notifications', icon: '🔔' },
    { path: '/community-moderation', label: 'Moderation', icon: '🛡️' },
    { path: '/group-tracker', label: 'Group Tracker', icon: '👥‍👥' },
    // { path: '/moderation-tester', label: 'Moderation Tester', icon: '🧪' },
    { path: '/hidden-users', label: 'Hidden Users', icon: '👻' },
    { path: '/reports', label: 'Reports', icon: '🚨' },
    { path: '/contacts', label: 'Contacts', icon: '📧' },
    { path: '/subscriptions', label: 'Subscriptions', icon: '💳' },
    { path: '/analytics', label: 'Analytics', icon: '📈' },
    { path: '/regional-insights', label: 'Regional Insights', icon: '🌍' },
  ];

  const roleAccess = {
    SUPER_ADMIN: new Set(menuItems.map((item) => item.path)),
    SUPPORT_ADMIN: new Set(['/dashboard', '/users', '/reports', '/contacts']),
    VERIFICATION_ADMIN: new Set(['/dashboard', '/verifications', '/verified-users', '/community-moderation']),
    CONTENT_MODERATOR: new Set([
      '/dashboard',
      '/community-moderation',
      '/moderation-tester',
      '/hidden-users',
      '/app-promotions',
      '/push-notifications',
      '/group-tracker',
    ]),
    REPORTING_ADMIN: new Set(['/dashboard', '/reports', '/contacts', '/analytics', '/impressions', '/user-activity']),
    COMPLIANCE_AUDITOR: new Set(['/dashboard', '/deleted-accounts']),
    ANALYTICS_ADMIN: new Set(['/dashboard', '/analytics', '/impressions', '/regional-insights', '/user-activity']),
  };

  const allowedPaths = roleAccess[adminRole] || roleAccess.SUPER_ADMIN;
  const visibleMenuItems = menuItems.filter((item) => allowedPaths.has(item.path));

  return (
    <div className={`min-h-screen ${colors.background}`}>
      {/* Sidebar */}
      <div
  className={`fixed inset-y-0 left-0 z-50 ${colors.card} shadow-lg transition-transform duration-300 ${
    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
  } w-64 flex flex-col overflow-hidden`}
>
<div className="flex flex-col h-screen overflow-hidden">
          {/* Logo */}
          <div className={`p-6 ${colors.border} border-b`}>
            <h1 className="text-2xl font-extrabold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              StrapIn Admin
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-thin">
            {visibleMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg'
                    : `${colors.textSecondary} ${colors.hover}`
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className={`p-4 ${colors.border} border-t`}>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
            >
              <span>🚪</span>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Top Bar */}
        <header className={`${colors.card} shadow-sm sticky top-0 z-40`}>
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`${colors.textSecondary} hover:text-pink-500 transition-colors`}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <span className={`text-sm ${colors.textSecondary}`}>Admin Portal</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 pt-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
